import { Component, Input, OnInit, ViewEncapsulation, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { FormInviteType } from "@app/forms/enums/FormInviteType";
import { Form } from "@app/forms/models";
import { FormResultStatus } from "@app/forms/models/result/FormResultStatus";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { Pages } from "@constants/pages/Pages";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { FormPermissionModel } from "@models/permissions/FormPermissionModel";
import { TabService } from "@services/tabs/TabService";
import { FormsDataService } from "@app/forms/services/forms-data.service";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { firstValueFrom } from "rxjs";
import { DebounceInputEventDirective } from "@app/shared/directives/debounce-input-event.directive";
import { InputTooltipDirective } from "@app/shared/directives/input-tooltip.directive";
import { DateFormatPipe } from "@app/shared/pipes/date-format.pipe";
import { GenericListManagerComponent } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.component";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/forms/i18n/scope";

@Component({
    selector: "form-status",
    templateUrl: "./form-status.component.html",
    styleUrls: ["./form-status.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [
        FormsModule,
        MatTooltipModule,
        DebounceInputEventDirective,
        InputTooltipDirective,
        DateFormatPipe,
        GenericListManagerComponent,
        TranslatePipe,
    ],
    viewProviders: [provideTranslationScope(scope)],
})
export class FormStatusComponent implements OnInit {
    private _formDS = inject(FormsDataService);
    private pages = inject(Pages);
    private tabService = inject(TabService);
    private _promptService = inject(CerrixPromptService);
    private translation = injectTranslation(scope);

    @Input() categories: IdNameCombination[];
    @Input() permissions: FormPermissionModel;

    formCategories: { category: string; forms: Form[]; hidden: boolean }[];
    filter = "";

    ngOnInit() {
        this.loadOverview();
    }

    async loadOverview() {
        this.formCategories = null;
        await this.translation.ready();

        firstValueFrom(this._formDS
            .getStatusOverview())
            .then((frms) => {
                this.setupConfigs(frms);
                this.setupFormCategories(frms);
            });
    }

    setupConfigs(forms: Form[]) {
        forms.forEach((form) => {
            form._genericConfig = <GenericListConfig>{
                idProp: "ID",
                data: form.ResultStatus.map((rs) => Object.assign(rs, { title: form.Title })),
                limitViewTo: 5,

                name: form.Title,
                hideHeader: true,
                isSortable: true,

                emptyMessage: this.translation.t("formStatus.noInviteesMessage"),
                maxHeight: "300px",

                overviewRowActions: [
                    {
                        icon: "fas fa-trash",
                        tooltip: this.translation.t("formStatus.deleteInviteTooltip"),
                        showProperty: "allowDelete",
                        clickEvent: (row) => {
                            this.deleteInvite(row);
                        },
                    },
                ],

                fields: [
                    {
                        fieldName: "title",
                        prettyName: this.translation.t("formStatus.titleField"),
                        fieldType: GenericListFieldType.Text,
                        hideInOverview: true,
                        includeInSearch: true,
                    },
                    {
                        fieldName: "starterUser",
                        prettyName: this.translation.t("formStatus.invitedUserField"),
                        fieldType: GenericListFieldType.Text,
                    },
                    {
                        fieldName: "dateInvited",
                        prettyName: this.translation.t("formStatus.dateInvitedField"),
                        fieldType: GenericListFieldType.DateTime,
                    },
                    {
                        fieldName: "dateInvitedDayCount",
                        prettyName: this.translation.t("formStatus.invitedDaysAgoField"),
                        fieldType: GenericListFieldType.Text,
                    },
                    {
                        fieldName: "status",
                        prettyName: this.translation.t("formStatus.statusField"),
                        fieldType: GenericListFieldType.Text,
                    },
                    {
                        fieldName: "currentUser",
                        prettyName: this.translation.t("formStatus.currentUserField"),
                        fieldType: GenericListFieldType.Text,
                    },
                    {
                        fieldName: "allowDelete",
                        fieldType: GenericListFieldType.CheckBox,
                        hideInEdit: true,
                        hideInOverview: true,
                    },
                ],

                editOverride: (row) => {
                    this.openEntry(form, row);
                },
            };

            form._hasOpenInvites =
                form.ResultStatus &&
                form.ResultStatus.filter((x) => x.inviteCompleted !== true).length > 0;
        });
    }

    setupFormCategories(forms: Form[]) {
        this.formCategories = forms
            .map((f) => f.Category)
            .distinct()
            .map((c) => {
                const catForms = forms.filter((f) => f.Category === c);
                const catName = catForms[0].CategoryName;

                return {
                    category: catName,
                    forms: catForms,
                    hidden: false,
                };
            });
    }

    async openEntry(form: Form, row: FormResultStatus) {
        await this.translation.ready();
        if (row.allowOpening) {
            const tab = this.tabService.getTab(
                this.pages.FormEntry,
                row.resultID,
                this.translation.t("formStatus.entryTabName", { title: form.Title }),
                { formid: form.ID }
            );
            this.tabService.addTab(tab);
        }
    }

    async openRequiredInvite(form: Form) {
        await this.translation.ready();
        this.tabService.generateTab(
            this.pages.FormInvite,
            form.ID,
            this.translation.t("formStatus.inviteTabName", { title: form.Title }),
            {
                targetedinvitationtype: FormInviteType.RequiredInvitation,
            }
        );
    }

    async openRemindInvite(form: Form) {
        await this.translation.ready();
        this.tabService.generateTab(
            this.pages.FormInvite,
            form.ID,
            this.translation.t("formStatus.inviteTabName", { title: form.Title }),
            {
                targetedinvitationtype: FormInviteType.ReminderInvitation,
            }
        );
    }

    async openOverview(form: Form) {
        await this.translation.ready();
        this.tabService.generateTab(
            this.pages.FormOverview,
            form.ID,
            this.translation.t("formStatus.resultsTabName", { title: form.Title })
        );
    }

    async deleteInvite(row) {
        await this.translation.ready();
        this._promptService
            .confirm(
                this.translation.t("formStatus.deleteInviteTitle"),
                this.translation.t("formStatus.deleteInviteMessage", { user: row.starterUser })
            )
            .onConfirm()
            .subscribe(() => {
                firstValueFrom(this._formDS
                    .deleteInvite(row.ID))
                    .then(() => {
                        this.loadOverview();
                    });
            });
    }

    filterTrigger() {
        this.formCategories.forEach((cat) => {
            cat.hidden = true;
            cat.forms.forEach((form) => {
                form._genericConfig.onSearch(this.filter);
                if (form._genericConfig._visibleRowCount > 0 || !this.filter) {
                    cat.hidden = false;
                }
            });
        });
    }
}
